import { useState, useEffect } from 'react';
import { QuizQuestion, QuestionType, CreateQuestionInput } from '../../../types/elearning';
import Input from '../../ui/Input';
import Select from '../../ui/Select';
import Button from '../../ui/Button';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';

interface QuestionEditorProps {
  question?: QuizQuestion;
  onSave: (input: CreateQuestionInput) => void;
  onCancel: () => void;
}

export default function QuestionEditor({ question, onSave, onCancel }: QuestionEditorProps) {
  const [questionText, setQuestionText] = useState(question?.question || '');
  const [type, setType] = useState<QuestionType>(question?.type || QuestionType.MULTIPLE_CHOICE);
  const [points, setPoints] = useState(question?.points || 1);
  const [explanation, setExplanation] = useState(question?.explanation || '');

  // Multiple choice state
  const [options, setOptions] = useState<Array<{ text: string; isCorrect: boolean }>>(
    question?.options || [{ text: '', isCorrect: false }, { text: '', isCorrect: false }]
  );

  // True/False state
  const [correctAnswer, setCorrectAnswer] = useState<boolean | undefined>(
    question?.correctAnswer !== undefined && typeof question.correctAnswer === 'boolean'
      ? question.correctAnswer
      : undefined
  );

  // Short answer state
  const [answerKey, setAnswerKey] = useState(question?.answerKey || '');

  useEffect(() => {
    if (question) {
      setQuestionText(question.question);
      setType(question.type);
      setPoints(question.points);
      setExplanation(question.explanation || '');
      
      if (question.options) {
        setOptions(question.options);
      }
      if (typeof question.correctAnswer === 'boolean') {
        setCorrectAnswer(question.correctAnswer);
      }
      if (question.answerKey) {
        setAnswerKey(question.answerKey);
      }
    } else {
      // Reset for new question
      setQuestionText('');
      setType(QuestionType.MULTIPLE_CHOICE);
      setPoints(1);
      setExplanation('');
      setOptions([{ text: '', isCorrect: false }, { text: '', isCorrect: false }]);
      setCorrectAnswer(undefined);
      setAnswerKey('');
    }
  }, [question]);

  const handleAddOption = () => {
    setOptions([...options, { text: '', isCorrect: false }]);
  };

  const handleOptionChange = (index: number, text: string) => {
    const newOptions = [...options];
    newOptions[index].text = text;
    setOptions(newOptions);
  };

  const handleToggleCorrect = (index: number) => {
    const newOptions = [...options];
    // For multiple choice, only one can be correct
    newOptions.forEach((opt, i) => {
      opt.isCorrect = i === index;
    });
    setOptions(newOptions);
  };

  const handleRemoveOption = (index: number) => {
    if (options.length > 2) {
      setOptions(options.filter((_, i) => i !== index));
    }
  };

  const handleSave = () => {
    if (!questionText.trim()) {
      alert('Please enter a question');
      return;
    }

    const input: CreateQuestionInput = {
      quizId: question?.quizId || '',
      question: questionText,
      type,
      order: question?.order || 0,
      points,
      explanation: explanation.trim() || undefined,
    };

    if (type === QuestionType.MULTIPLE_CHOICE) {
      const validOptions = options.filter((opt) => opt.text.trim());
      if (validOptions.length < 2) {
        alert('Please add at least 2 options');
        return;
      }
      const hasCorrect = validOptions.some((opt) => opt.isCorrect);
      if (!hasCorrect) {
        alert('Please mark at least one option as correct');
        return;
      }
      input.options = validOptions;
    } else if (type === QuestionType.TRUE_FALSE) {
      if (correctAnswer === undefined) {
        alert('Please select the correct answer');
        return;
      }
      input.correctAnswer = correctAnswer;
    } else if (type === QuestionType.SHORT_ANSWER) {
      if (!answerKey.trim()) {
        alert('Please enter an answer key or keywords');
        return;
      }
      input.answerKey = answerKey.trim();
    }

    // Save the question
    onSave(input);
    
    // Note: Form reset is handled by parent component (QuizBuilder)
    // which will set a new empty question, triggering useEffect to reset form
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Question Text *
        </label>
        <textarea
          value={questionText}
          onChange={(e) => setQuestionText(e.target.value)}
          rows={3}
          className="w-full px-4 py-3 border border-gray-300 rounded-md focus:border-primary focus:ring-2 focus:ring-primary focus:ring-offset-0"
          placeholder="Enter your question here..."
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Question Type *
          </label>
          <Select
            value={type}
            onChange={(e) => setType(e.target.value as QuestionType)}
            options={[
              { value: QuestionType.MULTIPLE_CHOICE, label: 'Multiple Choice' },
              { value: QuestionType.TRUE_FALSE, label: 'True/False' },
              { value: QuestionType.SHORT_ANSWER, label: 'Short Answer' },
            ]}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Points *
          </label>
          <Input
            type="number"
            value={points}
            onChange={(e) => setPoints(parseInt(e.target.value) || 1)}
            min={1}
          />
        </div>
      </div>

      {/* Multiple Choice Options */}
      {type === QuestionType.MULTIPLE_CHOICE && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <label className="block text-sm font-medium text-gray-700">
              Options *
            </label>
            <Button variant="tertiary" size="sm" onClick={handleAddOption}>
              <PlusIcon className="h-4 w-4 mr-1" />
              Add Option
            </Button>
          </div>
          <div className="space-y-2">
            {options.map((option, index) => (
              <div key={index} className="flex items-center gap-2">
                <Input
                  type="text"
                  value={option.text}
                  onChange={(e) => handleOptionChange(index, e.target.value)}
                  placeholder={`Option ${index + 1}`}
                  className="flex-1"
                />
                <button
                  type="button"
                  onClick={() => handleToggleCorrect(index)}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    option.isCorrect
                      ? 'bg-green-600 text-white hover:bg-green-700'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  {option.isCorrect ? 'Correct' : 'Mark Correct'}
                </button>
                {options.length > 2 && (
                  <button
                    type="button"
                    onClick={() => handleRemoveOption(index)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                    aria-label="Remove option"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* True/False Options */}
      {type === QuestionType.TRUE_FALSE && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Correct Answer *
          </label>
          <div className="flex gap-4">
            <button
              type="button"
              onClick={() => setCorrectAnswer(true)}
              className={`flex-1 px-4 py-3 rounded-md font-medium transition-colors ${
                correctAnswer === true
                  ? 'bg-green-600 text-white hover:bg-green-700'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              True
            </button>
            <button
              type="button"
              onClick={() => setCorrectAnswer(false)}
              className={`flex-1 px-4 py-3 rounded-md font-medium transition-colors ${
                correctAnswer === false
                  ? 'bg-green-600 text-white hover:bg-green-700'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              False
            </button>
          </div>
        </div>
      )}

      {/* Short Answer */}
      {type === QuestionType.SHORT_ANSWER && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Answer Key / Keywords *
          </label>
          <Input
            type="text"
            value={answerKey}
            onChange={(e) => setAnswerKey(e.target.value)}
            placeholder="Enter correct answer or keywords (comma-separated)"
          />
          <p className="text-xs text-gray-500 mt-1">
            Students' answers will be checked against these keywords
          </p>
        </div>
      )}

      {/* Explanation */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Explanation (Optional)
        </label>
        <textarea
          value={explanation}
          onChange={(e) => setExplanation(e.target.value)}
          rows={2}
          className="w-full px-4 py-3 border border-gray-300 rounded-md focus:border-primary focus:ring-2 focus:ring-primary focus:ring-offset-0"
          placeholder="Explanation shown after quiz submission..."
        />
      </div>

      {/* Actions */}
      <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
        <Button variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave}>
          Save Question
        </Button>
      </div>
    </div>
  );
}
